import { noop } from "lodash";
import { IframeHTMLAttributes } from "react";
import * as React from "react";
import { useDispatch } from "react-redux";

import { SafeIframe } from "components/safe-iframe/SafeIframe";
import { IFRAME_SCOPES } from "constants/integration-widget";
import { updateQueryString } from "helpers/url";
import { useLatest } from "hooks/use-latest";
import { IConnection, IInboxMessage, connectionPool, createConnection } from "services/widget";
import { WidgetIntegrationActions } from "store/actions";
import { IWidget } from "store/entities/applications";
import { ApplicationsActions } from "store/features/applications/actions";

import { iframeStyles } from "./styles";

interface IProps extends IframeHTMLAttributes<HTMLIFrameElement> {
  widget: IWidget;
  onConnected?(): void;
  onReconnected?(): void;
  onDisconnected?(): void;
  onMessage?(message: IInboxMessage): void;
  onInboxMessage?(message: IInboxMessage, connectionId: string): void;
}

type ConnectionCallbacks = Pick<
  IProps,
  "onConnected" | "onReconnected" | "onDisconnected" | "onMessage" | "onInboxMessage"
>;

const WidgetIframe: React.FC<IProps> = ({
  widget,
  onConnected = noop,
  onReconnected = noop,
  onDisconnected = noop,
  onMessage = noop,
}) => {
  const dispatch = useDispatch();
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const connectionCallbacks = useLatest<ConnectionCallbacks>({
    onConnected,
    onReconnected,
    onDisconnected,
    onMessage,
    onInboxMessage: (message, connectionId) =>
      dispatch(WidgetIntegrationActions.receiveInboxMessage(message, connectionId)),
  });

  const { id: widgetId, url: widgetUrl } = widget;

  const iframeSrc = updateQueryString(widgetUrl, "plugin_id", widgetId);

  React.useEffect(() => {
    let connection: IConnection | null = null;
    let connectionCanceled = false;
    const handleDisconnect = connectionCallbacks.current.onDisconnected;
    const opts = {
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      onReconnect: () => {
        const { onReconnected: onReconnectedRef } = connectionCallbacks.current;
        if (onReconnectedRef) {
          onReconnectedRef();
        }
      },
    };

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function handleConnectionMessage(message: IInboxMessage) {
      if (!connection) {
        return;
      }
      connectionCallbacks?.current?.onInboxMessage?.(message, connection?.connectionId);
      if (connectionCallbacks.current.onMessage) {
        connectionCallbacks.current.onMessage(message);
      }
    }

    if (!iframeRef.current) {
      return;
    }

    createConnection(iframeRef.current, iframeSrc, opts).then((newConnection: IConnection) => {
      if (connectionCanceled) {
        return;
      }

      connectionPool.add(widgetId, newConnection);
      newConnection.initialize();
      newConnection.subscribe(handleConnectionMessage);
      dispatch(ApplicationsActions.connectionEstablished(widgetId, newConnection.connectionId));
      connection = newConnection;
      connectionCallbacks.current.onConnected?.();
    });

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      if (connection) {
        connectionPool.remove(connection.connectionId);
        connection.destroy();
        connection = null;
        handleDisconnect?.();
      } else {
        connectionCanceled = true;
      }
    };
    /*
  keep in mind that this effect MUST NOT be called
  until widgetId has changed - otherwise parent & child iframe 
  will have different postMessage connections
  */
  }, [widgetId]); // eslint-disable-line react-hooks/exhaustive-deps

  return <SafeIframe key={widgetId} ref={iframeRef} src={iframeSrc} css={iframeStyles} allow={IFRAME_SCOPES} />;
};

export default WidgetIframe;
